import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { Group, GroupMember, Mote, GroupMemberState, GroupEvent, DatabaseModel, GroupEventType } from '../../models';
import { GroupService } from '../../providers';

/**
 * Modal displayed when waiting for member response to group requests.
 * Allows group members to mark themselves ready for the requested group event.
 */
@Component({
    selector: 'group-waiting',
    templateUrl: './group-waiting.component.html',
    styleUrls: ['./group-waiting.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class GroupWaitingComponent implements OnInit, OnChanges {

    /** The group members */
    public members: GroupMember[] = [];
    /** Count of members ready for the requested activity */
    public membersReady: number = 0;
    /** Flag to determine whether the current user is ready */
    public isCurrentUserReady: boolean = false;
    /** Flag to determine whether a grace period is active. This disables the button which emits the `onReady` event */
    public isGracePeriod: boolean = false;
    /** Export `GroupEventType` to template */
    public GroupEventType = GroupEventType;

    /** The group */
    @Input() group: Group = null;
    /** Key for the current user */
    @Input() currentUserKey: string = null;
    /** The `GroupEvent` which spawned the popup */
    @Input() event: GroupEvent = null;
    /** The Mote associated with the event which spawned the popup */
    @Input() mote: Mote = null;

    /** Event fired when the user selects *Skip*  */
    @Output() onSkip = new EventEmitter<void>();
    /** Event fired when the user selects *I'm ready* */
    @Output() onReady = new EventEmitter<void>();
    /** Event fired when the entire group is ready for playback */
    @Output() onCompletion = new EventEmitter<void>();

    constructor(private groupService: GroupService) { }

    ngOnInit() {
        // If the current user initiated the group event, introduce a short grace period before allowing click on the button which emits the `onReady` event
        // This decreases the risk of the current user starting the quiz alone, in cases where the state of the other group members have not changed to WAITING (e.g. due to network issues)
        if(this.currentUserKey == this.event?.userKey) {
            this.isGracePeriod = true;
            setTimeout(() => this.isGracePeriod = false, 2000);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Only run if the group provided via input has changed
        if(!changes.group) return;

        // Update the local members variable
        this.members = DatabaseModel.mapToArray(this.group?.members);

        // Check how many group members are ready
        const readyStates = [GroupMemberState.READY, GroupMemberState.IDLE];
        this.membersReady = this.members.filter((member) => (readyStates.includes(member.state))).length;

        // Check if the current user is ready
        const currentUser = this.members.find((member) => member.key == this.currentUserKey);
        this.isCurrentUserReady = currentUser?.state == GroupMemberState.READY;

        // If all members are ready, fire the `onCompletion` event
        if(this.membersReady == this.members.length) this.onCompletion.emit();
    }

    /**
       * Remove a group member
       * @param userKey user key for the member
       */
    public removeMember(userKey: string): void {
        // Remove the group member
        this.groupService.removeMember(this.group.key, userKey);
    }
}
